<template>
    <div>
        <p class="alert alert-danger" v-if="hasError('base')">
            <i class="fa fa-times-circle mr-2"></i><b class="mr-2">Stripe message</b><br/>- {{ hasError('base') }}
        </p>
        <form-title>Required</form-title>
        <input-text type="text"
            v-model="form.nickname"
            field="nickname"
            :form="form"
            label="Description"
            placeholder="A brief description of the plan, hidden from customers">
        </input-text>
        <transition name="fade-in-down">
            <div class="form-row" v-if="isCustom">
                <div class="col-6">
                    <input-text type="number"
                        v-model="form.interval_count"
                        min="1"
                        field="interval_count"
                        :form="form" label="Number of:">
                    </input-text>
                </div>
                <div class="col-6">
                    <label>&nbsp;</label>
                    <select class="form-control"
                        v-model="form.interval"
                        name="interval"
                        :class="{ 'is-invalid': hasError('interval') }">
                        <option value="day">days</option>
                        <option value="week">weeks</option>
                        <option value="month">months</option>
                    </select>
                </div>
                <span class="mb-2 invalid-feedback">
                    {{ hasError('interval') }}
                </span>
            </div>
        </transition>
        <transition name="fade-in-down">
            <div class="form-group" v-if="!isCustom">
                <label>Interval</label>
                <select class="form-control"
                    v-model="form.interval"
                    name="custom"
                    :class="{ 'is-invalid': hasError('interval') }">
                    <option v-for="interval in intervals" :value="interval.value">
                        {{ interval.name | capitalize }}
                    </option>
                </select>
                <span class="mb-2 invalid-feedback">
                    {{ hasError('interval') || hasError('custom') }}
                </span>
                <div class="alert alert-info">
                    <div class="small">By default the billing interval is one. Example: 1 Year</div>
                </div>
            </div>
        </transition>

        <label class="mt-3">Custom interval&nbsp;&nbsp;</label>
        <toggle-button class="mb-0"
            v-model="isCustom"
            :width="50"
            :height="20"
            :sync="isCustom"
            @change="clearIntervalCount"
            :color="{checked: '#0374d7'}">
        </toggle-button>

        <form-title>Price</form-title>
        <div class="form-row">
            <div class="col"><label>Price per unit</label></div>
            <div class="col"><label>Quantity</label></div>
        </div>
        <div class="form-row mb-2" v-for="(tier, index) of tiers" :key="tier.up_to + tier.amount + index">
            <div class="col">
                <input class="form-control" placeholder="Price per unit" v-model="tier.amount" v-money="money" />
                <span class="mb-2 invalid-feedback">
                    {{ hasError(`tiers.${index}.amount`) }}
                </span>
            </div>
            <div class="col">
                <div class="d-flex align-center">
                    <input class="form-control" type="number" min="1" placeholder="Quantity"
                        v-model="tier.up_to" v-if="tier.up_to !== 'inf'" />
                    <span class="mt-1 ml-1" v-if="index &gt; 0 &amp;&amp; index &lt; tiers.length - 1">
                        <i class="fa fa-fw fa-times cursor-pointer" @click="removeTier(index)"></i>
                    </span>
                </div>
                <span class="mb-2 invalid-feedback">
                    {{ hasError(`tiers.${index}.up_to`) }}
                </span>
                <span v-if="tier.up_to === 'inf'">or more</span>
            </div>
        </div>
        <div class="form-group">
            <button class="btn btn-primary" @click="addTier">
                <i class="fa fa-plus mr-1"></i>New Tier
            </button>
        </div>
        <div class="alert alert-info">
            <div class="small">All the options below are optional.</div>
        </div>
        <form-title>Details</form-title>
        <input-text type="number"
            v-model="form.trial_period_days"
            field="trial_period_days"
            :form="form"
            label="Trial Days"
            placeholder="Default is 0"
        />
        <input-text type="text"
            v-model="form.plan_id"
            field="id"
            :form="form"
            label="Plan ID (Optional)"
            placeholder="Used to identify this plan when subscribing a customer"
        />
        <form-title>Features (Optional)</form-title>
        <label>Name</label>
        <input class="form-control mb-3" type="text" v-model="feature" placeholder="Unlimited Locations" />
        <button class="btn btn-primary mb-3" @click="addFeature(feature)" :disabled="enableFeature">
            <i class="fa fa-plus mr-1"></i>New Feature
        </button>
        <ul
            class="list-group">
            <li class="list-group-item" v-for="feature of features()">
                <span>{{ feature }}</span>
                <span class="float-right">
                    <i class="fa fa-fw fa-times cursor-pointer" @click="removeFeature(feature)"></i>
                </span>
            </li>
        </ul>
    </div>
</template>

<script>

import { HasForm } from '../../mixins'
import { VMoney } from 'v-money'

export default {
    mixins: [ HasForm ],

    props: {
        value: { required: true, type: Form },
    },

    data: () => ({
        base_route: 'api.plans',
        intervals: [
            { name: 'daily', value: 'day' },
            { name: 'weekly', value: 'week' },
            { name: 'monthly', value: 'month' },
            { name: 'every 3 months', value: 'quarter' },
            { name: 'every 6 months', value: 'semiannual' },
            { name: 'yearly', value: 'year' },
        ],
        feature: '',
        isCustom: false,
        money: { precision: 2, masked: false },
    }),

    created() {
        this.form = this.value || new Form();
        this.initForm();
    },

    watch: {
        value() {
            if (this.value) {
                this.form = this.value;
                this.initForm();
            }
        },

        'form.trial_period_days': function() {
            return this.form.trial_period_days !== 0
                ? this.form.trial_from_plan = true
                : this.form.trial_from_plan = null
        },

        isCustom() {
            if (this.isCustom) {
                this.form.interval_count = 1;
                this.form.interval = 'day';
            };
            this.form.has_custom = this.isCustom
        }
    },

    computed: {
        tiers() { return this.form.tiers; },
        enableFeature() { return this.feature === '' || this.feature.replace(/\s*/g, '') === ''; }
    },

    methods: {
        initForm() {
            this.form.metadata = {};
            this.form.tiers = [
                { amount: 0, up_to: 1 },
                { amount: 0, up_to: 'inf' },
            ];
            this.form.nickname          = '';
            this.form.interval          = 'day';
            this.form.trial_from_plan   = null;
            this.form.trial_period_days = 0;
            this.form.plan_id           = null;
            this.form.interval_count    = 1;
            this.form.has_custom        = false;
        },

        hasError(value) { return this.form.errors.get(value); },

        features() { return Object.keys(this.form.metadata); },

        addFeature(feature) {
            feature = feature.replace(/\s+/g, ' ');
            this.$set(this.form.metadata, feature, feature);
            this.feature = '';
        },

        removeFeature(feature) {
            this.$delete(this.form.metadata, feature);
            this.$forceUpdate();
        },

        addTier() {
            const index = this.form.tiers.length - 1;
            let lastTier = this.form.tiers[index];
            const tier = { amount: lastTier.amount, up_to: null };
            lastTier.amount = 0;
            this.form.tiers.splice(index, 0, tier);
            this.$forceUpdate();
        },

        removeTier(index) {
            this.form.tiers.splice(index, 1);
            this.$forceUpdate();
        },

        clearIntervalCount({ value }) {
            if (! value) {
                this.form.interval_count = 1;
            }
        }
    }
}
</script>
